"use server";
import prisma from "@/lib/db";
import { State } from "../types/form";
import { UserRepresentation } from "../types/keycloak";
import { _getClientToken, verifyServerSession } from "@/utils/auth";
import { generateUserLocalCacheHandle } from "@/utils/userLocalCache";

const _verifyEmail = async (id: string): Promise<State> => {
  const token = await _getClientToken();
  const rep = await fetch(
    `${process.env.KEYCLOACK_ADMIN_URL}/users/${id}/send-verify-email`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify({
        token: token,
      }),
    },
  );
  return {
    status: rep.status,
    message: rep.statusText,
  };
};

export const deleteMe = async (): Promise<State> => {
  const { loggedInUuid } = await verifyServerSession();
  const token = await _getClientToken();

  let rep = await fetch(
    `${process.env.KEYCLOACK_ADMIN_URL}/users/${loggedInUuid}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
    },
  );

  return {
    status: rep.status,
    message: rep.statusText,
  };
};

export const getMe = async (): Promise<State> => {
  const { loggedInUuid } = await verifyServerSession();
  const token = await _getClientToken();
  const rep = await fetch(
    `${process.env.KEYCLOACK_ADMIN_URL}/users/${loggedInUuid}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
    },
  );
  return {
    status: rep.status,
    message: rep.statusText,
    data: await rep.json(),
  };
};

export const updateMe = async (
  userRepresentation: UserRepresentation,
): Promise<State> => {
  const { loggedInUuid } = await verifyServerSession();
  const token = await _getClientToken();
  const rep = await fetch(
    `${process.env.KEYCLOACK_ADMIN_URL}/users/${loggedInUuid}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify(userRepresentation),
    },
  );

  if (!rep.ok) {
    const errorText = await rep.text();
    console.error("Keycloak update error:", rep.status, errorText);
  }

  return {
    status: rep.status,
    message: rep.statusText,
  };
};

export const resetMyPassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<State> => {
  const { decodedToken, loggedInUuid } = await verifyServerSession();

  const verifyResponse = await fetch(
    `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "password",
        client_id: process.env.KEYCLOAK_CLIENT_ID!,
        client_secret: process.env.KEYCLOAK_CLIENT_SECRET!,
        username: decodedToken.preferred_username || decodedToken.email,
        password: currentPassword,
      }),
    },
  );

  if (!verifyResponse.ok) {
    return {
      status: 401,
      message: "Current password is incorrect",
    };
  }

  const token = await _getClientToken();

  const rep = await fetch(
    `${process.env.KEYCLOACK_ADMIN_URL}/users/${loggedInUuid}/reset-password`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify({
        type: "password",
        temporary: false,
        value: newPassword,
      }),
    },
  );
  return {
    status: rep.status,
    message: rep.statusText,
  };
};

export const recoverMyPassword = async (email: string): Promise<State> => {
  const token = await _getClientToken();

  let rep = await fetch(
    `${process.env.KEYCLOACK_ADMIN_URL}/users?email=${email}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
    },
  );

  const body = await rep.json();

  const userId = body[0]?.id;

  if (body.length > 0) {
    await fetch(
      `${process.env.KEYCLOACK_ADMIN_URL}/users/${userId}/reset-password-email`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access_token}`,
        },
      },
    );
  }

  return {
    status: 200,
    message:
      "If an account exists with this email, a password reset link has been sent",
  };
};

export const signUp = async (userRepresentation: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  attributes: Record<string, string>;
}): Promise<State> => {
  const token = await _getClientToken();

  // Transform attributes to arrays for Keycloak
  const keycloakAttributes: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(userRepresentation.attributes)) {
    keycloakAttributes[key] = Array.isArray(value) ? value : [value];
  }

  const rep = await fetch(`${process.env.KEYCLOACK_ADMIN_URL}/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.access_token}`,
    },
    body: JSON.stringify({
      username: `${userRepresentation.firstName} ${userRepresentation.lastName}`,
      firstName: userRepresentation.firstName,
      lastName: userRepresentation.lastName,
      email: userRepresentation.email,
      attributes: keycloakAttributes,
      emailVerified: false,
      enabled: true,
      credentials: [{ type: "password", value: userRepresentation.password }],
    }),
  });
  const userId = rep.headers.get("location")?.split("/").pop();
  console.log("new user uuid", rep.headers.get("location")?.split("/").pop());

  if (rep.status === 201 && userId) {
    for (let attempt = 0; attempt < 6; attempt++) {
      try {
        // All users must have a UserLocalCache after a successful signup.
        const userLocal = await prisma.userLocalCache.create({
          data: {
            keycloakId: userId,
            firstName: userRepresentation.firstName,
            lastName: userRepresentation.lastName,
            handle: generateUserLocalCacheHandle(userRepresentation.firstName),
          },
        });

        if (userLocal.id) {
          break;
        }
      } catch (error) {
        if (attempt >= 5) {
          console.log(
            `Account created, but the creation of the related UserLocalCache entry failed after ${
              attempt + 1
            } attempts.`,
          );
          console.log(error);
        }
      }
    }
  }

  await _verifyEmail(userId as string);

  return {
    status: rep.status,
    message: rep.statusText,
    data: {
      userId: userId,
      firstName: userRepresentation.firstName,
      lastName: userRepresentation.lastName,
    },
  };
};
